import { Row } from '@tanstack/react-table';
import RadioGroupField from 'components/forms/radioGroupField/RadioGroupField';
import {
  DisplayPreArrivalFormField,
  PreArrivalFormFields,
} from 'pages/automations/preArrivalForm/PreArrivalFormTab.types';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import Button from 'components/button/Button';
import useDeleteCustomFieldModal from '../useDeleteCustomFieldModal';
import { FieldValidationCellStyled } from './FieldValidationCell.styles';

const FieldValidationCell = ({
  row,
}: {
  row: Row<DisplayPreArrivalFormField>;
}) => {
  const { t } = useTranslation();
  const { id, uid, name } = row.original;
  const { watch, setValue, getValues, resetField } = useFormContext();
  const { openDeleteModal } = useDeleteCustomFieldModal({ uid: uid!, name });

  const isCustomField = id === PreArrivalFormFields.CUSTOM;

  const isNewCustomField = !row.original.uid && isCustomField;
  const isActive = watch(
    isNewCustomField
      ? `newCustomFields[${name.toLowerCase()}].isActive`
      : `${isCustomField ? `customFields.${uid}` : id}.isActive`,
  );
  const handleDelete = () => {
    if (isNewCustomField) {
      // using setValue, we need to remove the entry from the newCustomFields object
      const newCustomFields = getValues('newCustomFields') || {};

      if (Object.keys(newCustomFields).length === 1) {
        resetField('newCustomFields', {
          defaultValue: null,
        });
      } else {
        delete newCustomFields[name.toLowerCase()];
        setValue('newCustomFields', newCustomFields, { shouldDirty: true });
      }
    } else {
      openDeleteModal();
    }
  };

  return (
    <FieldValidationCellStyled>
      {isActive ? (
        <RadioGroupField
          // new field created just to avoid boolean string conversions
          name={
            isNewCustomField
              ? `newCustomFields[${name.toLowerCase()}].validation`
              : `${isCustomField ? `customFields.${uid}` : id}.validation`
          }
          options={[
            {
              value: 'REQUIRED',
              label: t(
                'pageAutomations.preArrivalForm.informationTable.fieldValidations.required',
              ),
            },
            {
              value: 'OPTIONAL',
              label: t(
                'pageAutomations.preArrivalForm.informationTable.fieldValidations.optional',
              ),
            },
          ]}
        />
      ) : (
        t(
          'pageAutomations.preArrivalForm.informationTable.fieldValidations.hidden',
        )
      )}

      {isCustomField && (
        <Button
          bsStyle="link"
          onClick={handleDelete}
          data-testid="delete-custom-field-button"
        >
          {t('common.delete')}
        </Button>
      )}
    </FieldValidationCellStyled>
  );
};

export default FieldValidationCell;
